import { Box, Button, Skeleton, type SxProps } from '@mui/material'
import { type ColorKey, colors } from './Colors'
import LoginIcon from '@mui/icons-material/Login'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import LogoutIcon from '@mui/icons-material/Logout'
import RocketIcon from '@mui/icons-material/Rocket'
import { type EnrollmentData, NewEnrollmentStatus } from '../../core/types'
import { useNavigate, generatePath } from 'react-router'
import { useEffect, useState } from 'react'
import { sendGet, sendPost } from '../../hooks/use-fetch'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { toast } from 'react-toastify'
import { AcceptEnrollment } from './AcceptEnrollment'
import { ConfirmAction, ConfirmEnrollment } from './ConfirmEnrollment'
import StartEnrollmentModal from './EnrollmentActionModals/StartEnrollmentModal'
import { useRefresh, useRefreshDispatch } from '../../ContextProviders/RefreshProvider'
import ExitEnrollmentModal from './EnrollmentActionModals/ExitEnrollmentModal'
import DeclineEnrollmentModal from './EnrollmentActionModals/DeclineEnrollmentModal'
import { Modal } from '../Modal'
import * as AppRoutes from '../../Pages/Routes'
import { usePermissions } from '../../hooks/use-permissions'

const buttonSx: SxProps = {
  p: '8px 25px',
  fontWeight: 'bold',
  textTransform: 'none',
  gap: 1
}
enum DisplayedModal {
  Release = 'Release',
  Accept = 'Accept',
  Decline = 'Decline',
  Confirm = 'Confirm',
  Modify = 'Modify',
  WaitList = 'WaitList',
  Enroll = 'Enroll',
  Start = 'Start',
  Exit = 'Exit',
  Update = 'Update'
}

export function EnrollmentActionBar (props: { enrollmentId: number, childId?: number, serviceId: number }): JSX.Element {
  const array: JSX.Element[] = []
  const [displayedModal, setDisplayedModal] = useState<string | null>(null)
  const refreshTime = useRefresh()
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }
  const nav = useNavigate()

  const perms = usePermissions()
  const handleDisplayModal = async (source: string): Promise<void> => {
    if (source === DisplayedModal.Accept) {
      if (enrollmentData?.isMissingData ?? true) {
        toast.error('This enrollment is missing caregiver or location information and can not be processed.')
        return
      }
    }
    setDisplayedModal(source)
  }

  const handleCloseModal = (accepted: boolean): void => {
    if (accepted) {
      refresh()
    }
    setDisplayedModal(null)
  }

  const returnToDetailPage = (): void => {
    let detailPage = ''
    if (props.childId != null) {
      detailPage = `${generatePath(AppRoutes.CARENETWORK_MANAGEMENT_DETAILS, { id: String(props.childId) })}?tab=2`
    } else {
      detailPage = generatePath(AppRoutes.ENROLLMENT_MANAGEMENT_DETAILS, { serviceId: String(props.serviceId) })
    }
    nav(detailPage)
  }

  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData | null>(null)

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response } = await sendGet(`/NewEnrollment/GetActionStatus?id=${props.enrollmentId}`, {})
      setEnrollmentData(response)
    }
    void getData()
  }, [props.enrollmentId, refreshTime])
  const ReleaseEnrollment = async (): Promise<void> => {
    const { response, success, error } = await sendPost(`/NewEnrollment/ReleaseEnrollment?id=${props.enrollmentId}`, {})
    if (success) {
      toast.success(response.message)
      refresh()
      setDisplayedModal(null)
    } else {
      error.forEach(e => toast.error(e))
    }
  }

  const returnButton = <Button data-testid='ReturnButton' variant='outlined' sx={{ ...buttonSx }} onClick={returnToDetailPage} >Return</Button>
  const releaseButton = CreateButton({ color: 'enrolled', text: 'Release', icon: <LoginIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Release) } })
  const acceptButton = CreateButton({ color: 'processing', text: 'Process', icon: <CheckIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Accept) } })
  const declineButton = CreateButton({ color: 'declined', text: 'Decline', icon: <CloseIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Decline) } })
  const confirmButton = CreateButton({ color: 'enrolled', text: 'Confirm', icon: <CheckIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Confirm) } })
  const startButton = CreateButton({ color: 'started', text: 'Start', icon: <RocketIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Start) } })
  const exitButton = CreateButton({ color: 'exited', text: 'Exit', icon: <LogoutIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Exit) } })
  const waitlistButton = CreateButton({ color: 'waitListed', text: 'Wait List', icon: <AccessTimeFilledIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.WaitList) } })
  const modifySessionButton = CreateButton({ color: 'enrolled', text: 'Modify Session', icon: <EditOutlinedIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Modify) } })
  const updateSessionButton = CreateButton({ color: 'enrolled', text: 'Modify Session', icon: <EditOutlinedIcon />, onClick: async () => { await handleDisplayModal(DisplayedModal.Update) } })
  array.push(returnButton)
  if (enrollmentData != null && (perms.Enrollment_Edit || perms.CareNetwork_Enrollment_Edit)) {
    if (enrollmentData.statusId === NewEnrollmentStatus.PendingRelease) {
      array.push(declineButton)
      if (enrollmentData.validAge && enrollmentData.isNextInLine && enrollmentData.programId != null) {
        array.push(releaseButton)
      }
    } else if (enrollmentData.statusId === NewEnrollmentStatus.Referred) {
      array.push(declineButton)
      if (enrollmentData.validAge && enrollmentData.programId != null) {
        array.push(acceptButton)
        const acceptModal = <AcceptEnrollment enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Accept} onClose={handleCloseModal} />
        array.push(acceptModal)
      }
    } else if (enrollmentData.statusId === NewEnrollmentStatus.Processing) {
      if (enrollmentData.programVerified) {
        if (enrollmentData.sessionId == null) {
          array.push(modifySessionButton)
          const modifyModal = <ConfirmEnrollment action={ConfirmAction.Modify} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Modify} onClose={handleCloseModal} />
          array.push(modifyModal)
        } else if (enrollmentData.sessionIsFull) {
          array.push(waitlistButton)
          const waitListModal = <ConfirmEnrollment action={ConfirmAction.Waitlist} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.WaitList} onClose={handleCloseModal} />
          array.push(waitListModal)
        } else {
          array.push(confirmButton)
          const confirmModal = <ConfirmEnrollment action={ConfirmAction.Confirm} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Confirm} onClose={handleCloseModal} />
          array.push(confirmModal)
        }
      }
      array.push(declineButton)
    } else if (enrollmentData.statusId === NewEnrollmentStatus.Waitlisted) {
      if (enrollmentData.sessionIsFull) {
        array.push(modifySessionButton)
        const modifyModal = <ConfirmEnrollment action={ConfirmAction.Modify} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Modify} onClose={handleCloseModal} />
        array.push(modifyModal)
      } else {
        if (enrollmentData.programVerified) {
          array.push(confirmButton)
          const confirmModal = <ConfirmEnrollment action={ConfirmAction.Confirm} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Confirm} onClose={handleCloseModal} />
          array.push(confirmModal)
        }
      }
      array.push(exitButton)
    } else if (enrollmentData.statusId === NewEnrollmentStatus.Enrolled) {
      array.push(updateSessionButton)
      const modifyModal = <ConfirmEnrollment action={ConfirmAction.Update} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Update} onClose={handleCloseModal} />
      array.push(modifyModal)

      array.push(startButton)
      array.push(exitButton)
    } else if (enrollmentData.statusId === NewEnrollmentStatus.Started) {
      array.push(updateSessionButton)
      const modifyModal = <ConfirmEnrollment action={ConfirmAction.Update} enrollmentID={props.enrollmentId} open={displayedModal === DisplayedModal.Update} onClose={handleCloseModal} />
      array.push(modifyModal)

      array.push(exitButton)
    }
  }
  const closeModal = (): void => {
    setDisplayedModal(null)
  }
  if (enrollmentData == null) { return <Skeleton /> }
  return <>
    <Box data-testid='displayBox' key={'actionsContainer'} sx={{ display: 'flex', flexDirection: 'row', gap: 1, my: '5px', justifyContent: 'space-between' }}>
      <>
        {array.map(r => <>
          {r}
        </>)}
      </>
    </Box>
    {displayedModal === DisplayedModal.Release && <Modal
      onClose={closeModal}
      open={displayedModal === DisplayedModal.Release}
      onConfirm={ReleaseEnrollment}
      title='Release Enrollment'
      confirmationContent={<>Are you sure you want to release this enrollment?</>}
    />}
    {displayedModal === DisplayedModal.Start && <StartEnrollmentModal
      open={displayedModal === DisplayedModal.Start}
      enrollmentData={enrollmentData}
      onClose={() => { setDisplayedModal(null) }}
    />}

    {displayedModal === DisplayedModal.Exit && <ExitEnrollmentModal
      open={displayedModal === DisplayedModal.Exit}
      enrollmentData={enrollmentData}
      onClose={() => { setDisplayedModal(null) }}
    />}
    {displayedModal === DisplayedModal.Decline && <DeclineEnrollmentModal
      open={displayedModal === DisplayedModal.Decline}
      enrollmentData={enrollmentData}
      onClose={() => { setDisplayedModal(null) }}
    />}
  </>
}

function CreateButton (props: { text: string, color: ColorKey, icon: JSX.Element, onClick?: () => Promise<void> }): JSX.Element {
  return <Button onClick={props.onClick}
    key={props.text}
    data-testid={`${props.text.replaceAll(' ', '')}Button`}
    variant='outlined' sx={{
      ...buttonSx,
      backgroundColor: colors[props.color].fill,
      color: colors[props.color].textColor,
      ':hover': {
        backgroundColor: colors[props.color].lighter
      }
    }}
  >{props.text} {props.icon}</Button>
}
